<template>
  <div class="insights-card">
    <div class="insights-head">
      <span class="spark">&#10024;</span>
      <h5 class="insights-title">{{ title }}</h5>
    </div>
    <p class="insights-caption" v-if="caption">{{ caption }}</p>

    <!-- Loading state -->
    <div class="insights-loading" v-if="loading">
      <div class="preloader-wrapper small active">
        <div class="spinner-layer spinner-blue-only">
          <div class="circle-clipper left"><div class="circle"></div></div>
          <div class="gap-patch"><div class="circle"></div></div>
          <div class="circle-clipper right"><div class="circle"></div></div>
        </div>
      </div>
      <span>Crunching the numbers&hellip;</span>
    </div>

    <!-- Empty state -->
    <div class="insights-empty" v-else-if="!insights || insights.length === 0">
      <i class="material-icons">insights</i>
      <span>No notable patterns to flag right now.</span>
    </div>

    <!-- Insight rows -->
    <div v-else class="insights-list">
      <div
        v-for="(insight, i) in insights"
        :key="i"
        class="insight-row"
      >
        <span class="insight-icon" :class="toneClass(insight.tone)"></span>
        <div class="insight-body">
          <p class="insight-title">{{ insight.title }}</p>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p class="insight-text" v-html="insight.text"></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * InsightsPanel
 * -------------
 * Reusable, module-agnostic renderer for the {tone, title, text} insight
 * shape returned by any *AnalyticsController's insights() endpoint (see
 * App\Support\Insights on the backend). Any module — DTR, Leave, Training,
 * IPCR — can drop this component into its own dashboard and just pass its
 * own `insights` array; this component has no DTR-specific knowledge.
 *
 * Props:
 *   insights: Array<{ tone: 'good'|'warning'|'serious'|'critical', title: string, text: string }>
 *   loading:  Boolean — shows a spinner instead of the list while true
 *   title:    String  — panel heading, defaults to "Insights"
 *   caption:  String  — optional subheading under the title
 *
 * `insight.text` is rendered with v-html so callers can bold key figures
 * (e.g. "<b>24 min</b> avg"). Only pass text built from your own backend's
 * numbers/labels — never raw, unescaped user input — the same rule that
 * applies anywhere else in this app that uses v-html.
 */
export default {
  name: "InsightsPanel",
  props: {
    insights: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Insights",
    },
    caption: {
      type: String,
      default: "",
    },
  },
  methods: {
    toneClass(tone) {
      const allowed = ["good", "warning", "serious", "critical"];
      return allowed.includes(tone) ? `tone-${tone}` : "tone-warning";
    },
  },
};
</script>

<style scoped>
.insights-card {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(11, 11, 11, 0.06);
  padding: 20px 24px 8px;
  margin-bottom: 24px;
}
.insights-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}
.insights-head .spark {
  font-size: 1.05rem;
}
.insights-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
}
.insights-caption {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 2px 0 14px;
}

.insights-loading,
.insights-empty {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  color: #94a3b8;
  font-size: 0.88rem;
}
.insights-empty i {
  font-size: 1.6rem;
  color: #cbd5e1;
}

.insight-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 0;
  border-top: 1px solid #f1f5f9;
}
.insight-row:first-of-type {
  border-top: none;
}
.insight-icon {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}
.insight-icon.tone-good { background-color: #0ca30c; }
.insight-icon.tone-warning { background-color: #fab219; }
.insight-icon.tone-serious { background-color: #ec835a; }
.insight-icon.tone-critical { background-color: #d03b3b; }

.insight-body { min-width: 0; }
.insight-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 2px;
}
.insight-text {
  font-size: 0.84rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}
.insight-text :deep(b) {
  color: #1e293b;
  font-weight: 700;
}
</style>
